import { useId } from "react";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
} from "@/features/dashboard/metricFormatters";
import type {
  DashboardFinancialLoadSituation,
  DashboardOperationalLoadSituation,
} from "@/features/dashboard/types";
import "./DashboardLedger.css";

const LOAD_CSS = `
  .dashboard-ledger-load {
    min-width: 0;
    border-block: 1px solid var(--ledger-border);
    background: var(--ledger-surface);
  }
  .dashboard-ledger-load__header {
    min-height: 46px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--ledger-border);
  }
  .dashboard-ledger-load__title {
    margin: 0;
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: 1.125rem;
    line-height: 1;
  }
  .dashboard-ledger-load__total {
    flex: 0 0 auto;
    color: var(--ledger-muted);
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .dashboard-ledger-load__list { min-width: 0; padding: 0 16px 7px; }
  .dashboard-ledger-load__row {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 7px 10px;
    min-width: 0;
    padding: 12px 0;
    border-top: 1px solid var(--ledger-border);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
  }
  button.dashboard-ledger-load__row { cursor: pointer; }
  button.dashboard-ledger-load__row:hover { background: var(--ledger-surface-muted); }
  button.dashboard-ledger-load__row:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--ledger-accent);
    outline-offset: -2px;
  }
  .dashboard-ledger-load__row:first-child { border-top: 0; }
  .dashboard-ledger-load__label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    font-size: 0.8125rem;
    font-weight: 700;
  }
  .dashboard-ledger-load__marker {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: currentColor;
  }
  .dashboard-ledger-load__row[data-tone="neutral"] { color: var(--dashboard-semantic-neutral, var(--ledger-muted)); }
  .dashboard-ledger-load__row[data-tone="warning"] { color: var(--dashboard-semantic-warning, #9a6400); }
  .dashboard-ledger-load__row[data-tone="positive"] { color: var(--dashboard-semantic-positive, var(--ledger-accent)); }
  .dashboard-ledger-load__label-text { color: var(--ledger-text); }
  .dashboard-ledger-load__count {
    color: var(--ledger-text);
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: 1.35rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }
  .dashboard-ledger-load__count[data-length="xlong"] {
    font-size: 1rem;
    letter-spacing: -0.02em;
  }
  .dashboard-ledger-load__details {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .dashboard-ledger-load__percentage {
    color: currentColor;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
  }
  .dashboard-ledger-load__money {
    min-width: 0;
    color: var(--ledger-text);
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
  }
  .dashboard-ledger-load__money[data-length="xlong"] {
    font-size: 0.6875rem;
    letter-spacing: -0.02em;
  }
  .dashboard-ledger-load__track {
    grid-column: 1 / -1;
    height: 3px;
    overflow: hidden;
    background: var(--ledger-border);
  }
  .dashboard-ledger-load__fill {
    display: block;
    width: 100%;
    height: 100%;
    background: currentColor;
    transform-origin: left;
  }
  @media (max-width: 640px) {
    .dashboard-ledger-load__header,
    .dashboard-ledger-load__list { padding-inline: 12px; }
    .dashboard-ledger-load__row { padding-block: 11px; }
    .dashboard-ledger-load__money { font-size: 0.75rem; }
  }
  @media (max-width: 360px) {
    .dashboard-ledger-load__details { grid-template-columns: minmax(0, 1fr); }
    .dashboard-ledger-load__money { justify-self: start; }
  }
`;

type DashboardLoadStateKey = "not_started" | "in_progress" | "concluded";

type DashboardLoadSituationProps = (
  | { mode: "financial"; situation: DashboardFinancialLoadSituation }
  | { mode: "operational"; situation: DashboardOperationalLoadSituation }
) & {
  onSelectState?: (state: DashboardLoadStateKey) => void;
};

const statePresentation = [
  {
    key: "not_started",
    financialState: "not_started",
    operationalState: "not_started",
    label: "Não iniciadas",
    tone: "neutral",
  },
  {
    key: "in_progress",
    financialState: "in_progress",
    operationalState: "in_progress",
    label: "Em andamento",
    tone: "warning",
  },
  {
    key: "concluded",
    financialState: "concluded",
    operationalState: "done",
    label: "Concluídas",
    tone: "positive",
  },
] as const;

const percentageNumber = (value: string): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(100, Math.max(0, parsed));
};

export function DashboardLoadSituation(props: DashboardLoadSituationProps) {
  const total = formatDashboardCount(props.situation.total);
  const titleId = `dashboard-ledger-load-${useId()}`;

  return (
    <section
      className="dashboard-ledger dashboard-ledger-load"
      aria-labelledby={titleId}
    >
      <style>{LOAD_CSS}</style>
      <header className="dashboard-ledger-load__header">
        <h2 id={titleId} className="dashboard-ledger-load__title">
          Situação das cargas
        </h2>
        <span className="dashboard-ledger-load__total">
          {total.full} cargas
        </span>
      </header>
      <div className="dashboard-ledger-load__list">
        {statePresentation.map((presentation) => {
          const state =
            props.mode === "financial"
              ? props.situation.states.find(
                  (candidate) =>
                    candidate.state === presentation.financialState,
                )
              : props.situation.states.find(
                  (candidate) =>
                    candidate.state === presentation.operationalState,
                );
          const count = formatDashboardCount(state?.count ?? 0);
          const percentageValue = state?.percentage ?? "0";
          const percentage = formatDashboardPercent(percentageValue);
          const progress = percentageNumber(percentageValue);
          const money =
            props.mode === "financial" && state && "total_value_cents" in state
              ? formatDashboardCurrency(state.total_value_cents)
              : undefined;
          const rowLabel = `${presentation.label}: ${count.accessibleLabel} cargas, ${percentage.accessibleLabel}${money ? `, ${money.accessibleLabel}` : ""}`;

          const content = (
            <>
              <span className="dashboard-ledger-load__label">
                <span
                  className="dashboard-ledger-load__marker"
                  aria-hidden="true"
                />
                <span className="dashboard-ledger-load__label-text">
                  {presentation.label}
                </span>
              </span>
              <strong
                className="dashboard-ledger-load__count dashboard-ledger-number"
                data-length={count.full.length > 12 ? "xlong" : "regular"}
              >
                {count.full}
              </strong>
              <div className="dashboard-ledger-load__details">
                <span className="dashboard-ledger-load__percentage">
                  {percentage.full}
                </span>
                {money ? (
                  <strong
                    className="dashboard-ledger-load__money dashboard-ledger-number"
                    data-length={money.full.length > 20 ? "xlong" : "regular"}
                  >
                    {money.full}
                  </strong>
                ) : null}
              </div>
              <div
                className="dashboard-ledger-load__track"
                role="progressbar"
                aria-label={`${presentation.label}: ${percentage.accessibleLabel}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <span
                  className="dashboard-ledger-load__fill"
                  style={{ transform: `scaleX(${progress / 100})` }}
                />
              </div>
            </>
          );

          const shared = {
            className: "dashboard-ledger-load__row",
            "data-testid": `dashboard-load-${presentation.key}`,
            "data-layout": "aligned-row",
            "data-tone": presentation.tone,
            "aria-label": rowLabel,
          } as const;
          return props.onSelectState ? (
            <button
              {...shared}
              type="button"
              key={presentation.key}
              onClick={() => props.onSelectState?.(presentation.key)}
            >
              {content}
            </button>
          ) : (
            <div {...shared} key={presentation.key}>
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}
